import { z } from 'zod';
import { DEPARTMENT_DIVISION, DepartmentSchema, DivisionSchema, RoleGradeSchema, } from './headcount';
/**
 * Position ledger (headcount planning work package A3).
 *
 * One row = one budgeted head: a claim on headcount at
 * (department, grade, target month), priced at the rate card. A row is not a
 * hiring idea, not a team slot, and not a person (decision D10, 2026-07-11):
 *
 * - Hiring ideas stay in `hiring_spots` (workflow domain) as the demand pool.
 *   They are never ledger rows; the ledger only references a spot at fill.
 * - Planning quantities ("3 Specialists in May") are ledger rows with
 *   program/spot unbound. The deliberate vagueness of department-level
 *   planning is preserved: `program_id` stays null through planning.
 * - Binding happens at fill: when a candidate lands, the placement pairing
 *   (hire <-> spot, which is unchanged by the ledger) extends to consume a
 *   matching ledger row — that is when program_id, hiring_spot_id and
 *   confirmed_hire_id are stamped onto the row. Filling consumes an existing
 *   approved row; it never mints a new one.
 * - Small departments use the same schema traditionally: a row created with
 *   a role_title, walked through the lifecycle as a specific role.
 *
 * Postgres-shaped: flat scalar columns, ISO-8601 string timestamps, and one
 * append-only history array (a jsonb column after the planned data-plane
 * migration).
 */
// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
export const POSITION_STATUSES = [
    'planned',
    'approved',
    'open',
    'offer',
    'filled',
    'deferred',
    'cancelled',
];
export const PositionStatusSchema = z.enum(POSITION_STATUSES);
/**
 * Statuses that count as committed headcount for envelope math (A5):
 * everything from formal approval onward, excluding terminal exits.
 */
export const COMMITTED_POSITION_STATUSES = [
    'approved',
    'open',
    'offer',
    'filled',
];
/**
 * Allowed lifecycle transitions. Permissive by design at A3 — the governance
 * gate (envelope + band check at planned -> approved, req gate at
 * approved -> open) arrives server-side with A6. `approved -> offer/filled`
 * is deliberate: Performance fills consume approved rows directly without an
 * explicit `open` step until the req gate exists.
 */
export const POSITION_TRANSITIONS = {
    planned: ['approved', 'deferred', 'cancelled'],
    approved: ['open', 'offer', 'filled', 'planned', 'deferred', 'cancelled'],
    open: ['offer', 'filled', 'approved', 'deferred', 'cancelled'],
    offer: ['filled', 'open', 'deferred', 'cancelled'],
    filled: [],
    deferred: ['planned', 'approved', 'cancelled'],
    cancelled: [],
};
export function isAllowedPositionTransition(from, to) {
    return POSITION_TRANSITIONS[from].includes(to);
}
// ---------------------------------------------------------------------------
// Type (backfill / net-new)
// ---------------------------------------------------------------------------
export const POSITION_TYPES = ['backfill', 'net_new'];
export const PositionTypeSchema = z.enum(POSITION_TYPES);
// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------
/**
 * Permanent provenance, not migration debris: most live rows were seeded by
 * the A3 migration (2026-07) and keep their migration source forever, and the
 * ledger's sibling grouping ("head i of n from one planning cell") keys off
 * source + source_ref. Do not remove values while live docs carry them —
 * confirmed live 2026-07-16 (70 of 71 rows).
 */
export const POSITION_SOURCES = [
    'manual',
    'migration:planned_hires',
    'migration:confirmed_hires',
];
export const PositionSourceSchema = z.enum(POSITION_SOURCES);
export const PositionStatusChangeSchema = z.object({
    from: PositionStatusSchema.nullable(),
    to: PositionStatusSchema,
    at: z.string(),
    by: z.string(),
    by_name: z.string(),
    note: z.string().max(1000).optional(),
});
const MonthSchema = z.string().regex(/^\d{4}-\d{2}-01$/, 'Month must be YYYY-MM-01');
export const HiringPositionSchema = z
    .object({
    id: z.string().min(1),
    department: DepartmentSchema,
    division: DivisionSchema,
    role_grade: RoleGradeSchema,
    status: PositionStatusSchema,
    type: PositionTypeSchema.nullable(),
    target_start_month: MonthSchema.nullable(),
    role_title: z.string().max(200).nullable(),
    rationale: z.string().max(1000),
    owner_id: z.string().nullable(),
    owner_name: z.string().nullable(),
    rate_card_cost: z.number().nonnegative().nullable(),
    rate_card_fiscal_year: z.number().int().nullable(),
    program_id: z.string().nullable(),
    program_name: z.string().nullable(),
    hiring_spot_id: z.string().nullable(),
    confirmed_hire_id: z.string().nullable(),
    req_id: z.string().nullable(),
    exception_id: z.string().nullable(),
    source: PositionSourceSchema,
    source_ref: z.string().nullable(),
    status_history: z.array(PositionStatusChangeSchema),
    created_at: z.string(),
    created_by: z.string(),
    updated_at: z.string(),
    updated_by: z.string(),
})
    .superRefine((position, ctx) => {
    if (DEPARTMENT_DIVISION[position.department] !== position.division) {
        ctx.addIssue({
            code: 'custom',
            path: ['division'],
            message: `Division '${position.division}' does not match department '${position.department}'`,
        });
    }
});
export const CreateHiringPositionDataSchema = z.object({
    department: DepartmentSchema,
    role_grade: RoleGradeSchema,
    rationale: z.string().max(1000),
    type: PositionTypeSchema.nullable().optional(),
    target_start_month: MonthSchema.nullable().optional(),
    role_title: z.string().max(200).nullable().optional(),
    owner_id: z.string().nullable().optional(),
    owner_name: z.string().nullable().optional(),
    program_id: z.string().nullable().optional(),
    program_name: z.string().nullable().optional(),
});
